/**
 * The file tree that loads one folder at a time (RepositoryTree on a vfs-backed project). The tree stays the flat
 * list of items RepositoryTree always drew (`{ path, name, type: 'blob'|'tree', level, parent }`), but a folder's
 * children are only in it once the folder was opened, from vfs-service's `GET /tree/children`. These helpers are the
 * pure part: turning a listing into items, putting it into the tree, the rows to draw, and the folder markers.
 */

import { GIT_STATE } from './indexedDbFileStorage';

/** Entries asked for per folder: past it the folder shows a "Show more" row (vfs-service caps a listing at 5000). */
export const FOLDER_PAGE_SIZE = 1000;

/** Over this many rows only the ones on screen are drawn (RepositoryTree's own windowing). */
export const VIRTUALIZE_AT = 300;

export const parentFolderOf = (path) => (path && path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');

/** Every folder above `path`, outermost first: `a/b/c.js` → `['a', 'a/b']`. */
export function ancestorFolders(path) {
  const parts = String(path || '').split('/').filter(Boolean);
  return parts.slice(0, -1).map((_, index) => parts.slice(0, index + 1).join('/'));
}

/**
 * Tree items for a folder's listing. `folder` is the tree path ('' for the tree's root); `skip` holds names left out
 * (a service's own `.mia/` at its root: collaboration metadata, shown in Flow's panels, never in the tree).
 */
export function itemsFromListing(folder, entries, { skip = [] } = {}) {
  const level = folder ? folder.split('/').length : 0;
  return (entries || [])
    .filter((entry) => entry && entry.name && !skip.includes(entry.name))
    .map((entry) => {
      const path = folder ? `${folder}/${entry.name}` : entry.name;
      const item = { path, name: entry.name, type: entry.type === 'directory' ? 'tree' : 'blob', level, parent: folder || undefined };
      if (entry.size !== undefined) { item.size = entry.size; }
      // node_modules, dist and the like: drawn with their count, opened only when asked.
      if (entry.collapsed) {
        item.heavy = true;
        item.childCount = entry.childCount;
      }
      return item;
    });
}

const parentKey = (item) => item.parent || '';

/**
 * The tree with `folder`'s children replaced by `items` (a fresh listing): what is gone leaves, with everything that
 * was loaded under it, and what stays keeps its loaded children. `append`: one more page of the same folder.
 */
export function mergeFolderListing(tree, folder, items, { append = false } = {}) {
  const incoming = new Map(items.map((item) => [item.path, item]));
  const gone = [];
  const kept = [];
  for (const item of tree) {
    if (incoming.has(item.path)) { continue; } // replaced below
    if (!append && parentKey(item) === folder) {
      gone.push(`${item.path}/`);
      continue;
    }
    kept.push(item);
  }
  const survivors = gone.length ? kept.filter((item) => !gone.some((prefix) => item.path.startsWith(prefix))) : kept;
  return [...survivors, ...items];
}

/** The tree without `path` and everything under it (a folder that turned out not to exist anymore). */
export function withoutSubtree(tree, path) {
  const prefix = `${path}/`;
  return tree.filter((item) => item.path !== path && !item.path.startsWith(prefix));
}

const compareItems = (a, b) => {
  if (a.type !== b.type) { return a.type === 'tree' ? -1 : 1; }
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
};

/** Children of every folder ('' = the root), sorted the way the tree draws them: folders first, then by name. */
export function childrenIndex(tree) {
  const index = new Map();
  for (const item of tree) {
    const key = parentKey(item);
    if (!index.has(key)) { index.set(key, []); }
    index.get(key).push(item);
  }
  for (const children of index.values()) { children.sort(compareItems); }
  return index;
}

/**
 * The rows to draw, in order: a depth-first walk from the root that only enters open folders (and folders folded
 * into a compact chain, which count as open). An absorbed folder is not a row of its own. With `include` (a search),
 * exactly the items in it, wherever they are. `more` (folder → {cursor, remaining}): a folder only partly listed ends
 * with a `{type: 'more'}` row.
 */
export function buildRows(index, { expanded, absorbed = new Set(), include = null, more = new Map() }) {
  const rows = [];
  const moreRow = (folder) => ({
    type: 'more', path: `${folder}/\u0000more`, folder, level: folder ? folder.split('/').length : 0, parent: folder || undefined, ...more.get(folder),
  });
  const stack = [...(index.get('') || [])].reverse();
  if (!include && more.has('')) { stack.unshift(moreRow('')); }
  while (stack.length) {
    const item = stack.pop();
    if (item.type === 'more') {
      rows.push(item);
      continue;
    }
    let descend;
    if (include) {
      if (!include.has(item.path)) { continue; }
      rows.push(item);
      descend = item.type === 'tree';
    } else {
      if (!absorbed.has(item.path)) { rows.push(item); }
      descend = item.type === 'tree' && (expanded.has(item.path) || absorbed.has(item.path));
    }
    if (descend) {
      if (!include && more.has(item.path)) { stack.push(moreRow(item.path)); }
      const children = index.get(item.path);
      if (children) {
        for (let i = children.length - 1; i >= 0; i -= 1) { stack.push(children[i]); }
      }
    }
  }
  return rows;
}

/** The matches of a file-name search and every folder above them: what `buildRows` shows while searching. */
export function searchMatches(tree, query) {
  const lower = String(query || '').toLowerCase();
  const include = new Set();
  for (const item of tree) {
    if (item.name.toLowerCase().includes(lower) || item.path.toLowerCase().includes(lower)) {
      include.add(item.path);
      for (const folder of ancestorFolders(item.path)) { include.add(folder); }
    }
  }
  return include;
}

/** Every folder that has at least one of `paths` somewhere under it: a folder row's marker is then one lookup. */
export function foldersAbove(paths) {
  const folders = new Set();
  for (const path of paths) {
    for (const folder of ancestorFolders(path)) { folders.add(folder); }
  }
  return folders;
}

const isUnsent = (state) => state === GIT_STATE.MODIFIED || state === GIT_STATE.NEW || state === GIT_STATE.DELETED;
export const unsentPaths = (fileStates) => Object.keys(fileStates || {}).filter((path) => isUnsent(fileStates[path]));

/**
 * The listed tree with this browser's own unsent work on top (the local cache's git states): a new file not on
 * vfs-service yet shows up in its folder once that folder is loaded (with any new folders above it), a file marked
 * for deletion is drawn struck through, and the new/modified badges come from the cache too.
 */
export function withLocalState(tree, fileStates, isLoaded) {
  const states = fileStates || {};
  const present = new Set(tree.map((item) => item.path));
  const marked = tree.map((item) => {
    if (item.type !== 'blob') { return item; }
    const state = states[item.path];
    const isNew = state === GIT_STATE.NEW;
    const modified = state === GIT_STATE.MODIFIED;
    const deleted = state === GIT_STATE.DELETED;
    if (Boolean(item.isNew) === isNew && Boolean(item.modified) === modified && Boolean(item.deleted) === deleted) { return item; }
    return { ...item, isNew, modified, deleted };
  });
  const extra = [];
  for (const path of Object.keys(states)) {
    if (states[path] !== GIT_STATE.NEW || present.has(path)) { continue; }
    const folders = ancestorFolders(path);
    // Only under the deepest folder of its path that the tree has, once that one is loaded: a new file deeper down
    // shows when its folders are opened.
    let deepest = -1;
    folders.forEach((folder, index) => { if (present.has(folder)) { deepest = index; } });
    if (!isLoaded(deepest >= 0 ? folders[deepest] : '')) { continue; }
    for (const folder of folders.slice(deepest + 1)) {
      if (present.has(folder)) { continue; }
      present.add(folder);
      extra.push({ path: folder, name: folder.split('/').pop(), type: 'tree', level: folder.split('/').length - 1, parent: parentFolderOf(folder) || undefined, local: true });
    }
    present.add(path);
    extra.push({ path, name: path.split('/').pop(), type: 'blob', level: folders.length, parent: parentFolderOf(path) || undefined, isNew: true, local: true });
  }
  return extra.length ? [...marked, ...extra] : marked;
}

/** Tree paths (relative to the tree's scope) → the loaded folders to list again: each path's folder, or itself. */
export function foldersToRefresh(paths, isLoaded) {
  const folders = new Set();
  for (const path of paths) {
    if (isLoaded(path)) { folders.add(path); }
    const parent = parentFolderOf(path);
    if (isLoaded(parent)) { folders.add(parent); }
  }
  return [...folders];
}
